import { pick } from 'lodash';
import React from 'react';
import Modal from 'react-modal';
import { connect, Dispatch } from 'react-redux';
import { InjectedRouter } from 'react-router';
import { bindActionCreators } from 'redux';

import { withRouter } from 'app/context/Router';
import CurrentUser from 'auth/CurrentUser';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import ConfirmDialog from 'components/ConfirmDialog';
import Dialog from 'components/Dialog';
import { Message, Target } from 'entities/Message';
import EventTracking from 'lib/EventTracking';
import { clearSelection, saveEnvelope, Envelope } from 'reducers/roleBasedMessagingSearch';

import { ComposerContext, UIEventContext } from './ComposerContext';
import { MessageType } from './SplitComposer';
import SplitComposerDataContainer from './SplitComposerDataContainer';

type Props = {
  skipSelectRecipients: boolean;
  messageType: MessageType;
  onRequestHide: () => void;
  router: InjectedRouter;
  onComposerPage: boolean;
  draftAnnouncement?: Message;
  initialTargets?: Target[];
  onMessageSent?: (options: { scheduledTime?: Date; targets: Target[] }) => void;
};

type State = {
  isOpen: boolean;
  // Controls whether to show the "Discard selection?" modal on cancel
  significantChange: boolean;
  // Set when the user clicks "Continue" to move to the composer,
  // so we don't treat it as a cancel action in handleRequestHide
  isContinue: boolean;
};

type ReduxDispatchProps = {
  clearSelection: () => void;
  saveEnvelope: (x: Envelope) => void;
};

class SplitComposerModal extends React.Component<Props & ReduxDispatchProps, State> {
  // messageType is optional as a prop but is required in redux, so default it here
  static defaultProps: { messageType: MessageType } = { messageType: 'message' };

  state: State = {
    isOpen: false,
    significantChange: false,
    isContinue: false,
  };

  handleConfirm = () => {
    this.props.clearSelection();
    this.props.onRequestHide();
  };
  handleCancel = () => this.setState({ isOpen: false });

  generateHandleRequestHide = (uiEventContext: UIEventContext) => () => {
    if (!this.state.isContinue && !this.props.onComposerPage) {
      EventTracking.trackKey(`announcements.share_to.cancel.click`, uiEventContext);

      if (this.state.significantChange) {
        this.setState({ isOpen: true });
        return;
      }
    }
    this.props.onRequestHide();
  };

  handleSignificantChange = () => {
    if (!this.state.significantChange) {
      this.setState({ significantChange: true });
    }
  };

  handlePageChange = () => {
    this.setState({ isContinue: true });
    this.props.saveEnvelope(
      pick(this.props, ['draftAnnouncement', 'initialTargets', 'messageType', 'onMessageSent'])
    );
    this.props.onRequestHide();
    this.props.router.push('/compose');
  };

  render() {
    const { skipSelectRecipients } = this.props;

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isOpen} data-test="confirm-close-dialog">
          <ConfirmDialog
            hideCloseButton
            cancelButtonText="Cancel"
            actionButtonText="Discard"
            onCancel={this.handleCancel}
            onConfirm={this.handleConfirm}
            onRequestHide={this.handleCancel}
            title="Discard selection?"
          />
        </Modal>
        <ComposerContext.Consumer>
          {composerContext => {
            if (!CurrentUser.isSignedIn()) {
              return null;
            }

            return (
              <Dialog
                hideCloseButton={skipSelectRecipients}
                size="large"
                className="SplitComposerModal"
                onRequestHide={this.generateHandleRequestHide(composerContext.getUIEventContext())}
                box={false /* a boxed dialog has bigger font size than a box in a dialog */}>
                <Box
                  noBorder={skipSelectRecipients}
                  color={skipSelectRecipients ? 'none' : 'white'}>
                  <BoxBody full>
                    <SplitComposerDataContainer
                      {...this.props}
                      onPageChange={
                        this.props.onComposerPage ? this.props.onRequestHide : this.handlePageChange
                      }
                      onSignificantChangeMade={
                        skipSelectRecipients ? () => {} : this.handleSignificantChange
                      }
                    />
                  </BoxBody>
                </Box>
              </Dialog>
            );
          }}
        </ComposerContext.Consumer>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): any =>
  bindActionCreators({ clearSelection, saveEnvelope }, dispatch);

export { SplitComposerModal };
export default withRouter(connect(undefined, mapDispatchToProps)(SplitComposerModal));
